import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, CheckCircle, XCircle, MailWarning, Plus, AlertCircle } from 'lucide-react';
import { useAdminIdentityUsers, useCreateAdminUser } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { SectionHeader, EmptyState, Spinner, StatCard } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';
import { ROUTES } from '../../routes/constants';
import { toast } from '../../store';

// Status badge
function StatusBadge({ status }) {
  const map = {
    active:               'text-bull bg-bull/10 border-bull/30',
    blocked:              'text-bear bg-bear/10 border-bear/30',
    pending_verification: 'text-warn bg-warn/10 border-warn/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono ${map[status] ?? 'text-txt-muted border-bg-border'}`}>
      {status ?? '—'}
    </span>
  );
}

// Role badge list
function RoleList({ roles }) {
  if (!roles?.length) return <span className="text-txt-muted font-mono text-2xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span key={r} className="px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-2xs font-mono text-txt-secondary">
          {r}
        </span>
      ))}
    </div>
  );
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { data: users, isLoading } = useAdminIdentityUsers();
  const createUser = useCreateAdminUser();
  const [showCreate, setShowCreate] = useState(false);

  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const sessionRole  = useSessionStore((s) => s.role);
  const viewerId     = useSessionStore((s) => s.user?.id ?? null);
  const isOperatorViewer = !isSuperAdmin && sessionRole === 'operator';
  // The backend returns only the Operator's assigned customers; the self-row
  // exclusion is a display safeguard, not ownership logic.
  const allUsers = (Array.isArray(users) ? users : []).filter(
    (u) => !(isOperatorViewer && viewerId != null && u?.id === viewerId),
  );
  // operator: end_user only; admin: end_user + operator; super_admin: all
  const creatableRoles = isSuperAdmin
    ? ['end_user', 'operator', 'admin']
    : sessionRole === 'admin'
      ? ['end_user', 'operator']
      : ['end_user'];

  const [createError, setCreateError] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', role: 'end_user', operator_id: '' });

  // Operator dropdown: only for admin/super_admin creating end_user
  const showOperatorSelect = sessionRole === 'admin' && form.role === 'end_user';
  // Derive active operators from already-fetched user list (no extra API call)
  const operatorOptions = allUsers.filter(
    (u) => u.roles?.includes('operator') && u.status === 'active'
  );
  const noOperatorsAvailable = showOperatorSelect && operatorOptions.length === 0;

  const activeCount  = allUsers.filter((u) => u.status === 'active').length;
  const blockedCount = allUsers.filter((u) => u.status === 'blocked').length;

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');

    // Frontend validation: operator required when admin/super_admin creates end_user
    if (showOperatorSelect && !form.operator_id) {
      setCreateError('Operator is required when creating an end user.');
      return;
    }

    const payload = {
      full_name: form.full_name,
      email: form.email,
      phone: form.phone || undefined,
      roles: [form.role],
      ...(showOperatorSelect && form.operator_id ? { operator_id: Number(form.operator_id) } : {}),
    };

    try {
      const result = await createUser.mutateAsync(payload);
      if (result.welcome_email_sent) {
        toast.success('User created and welcome email sent');
      } else {
        toast.warning('User created, but welcome email could not be delivered');
      }
      setForm({ full_name: '', email: '', phone: '', role: 'end_user', operator_id: '' });
      setShowCreate(false);
    } catch (err) {
      setCreateError(err?.message || 'Failed to create user.');
    }
  };

  return (
    <div className="max-w-5xl space-y-5">
      <SectionHeader
        title="User Management"
        subtitle={isOperatorViewer ? `${allUsers.length} assigned customers` : `${allUsers.length} registered users`}
        actions={(
          <button onClick={() => setShowCreate((v) => !v)} className="btn-primary flex items-center gap-1.5 text-xs">
            <Plus size={14} /> {showCreate ? 'Close' : 'New User'}
          </button>
        )}
      />

      {showCreate && (
        <form onSubmit={handleCreate} className="panel p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Full Name</label>
              <input
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                className="input-base"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="input-base"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="input-base"
              />
            </div>
            <div className="space-y-1">
              <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Initial Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value, operator_id: '' }))}
                className="input-base"
              >
                {creatableRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {/* Operator dropdown — admin/super_admin creating end_user only */}
            {showOperatorSelect && (
              <div className="space-y-1 md:col-span-2">
                <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">
                  Operator <span className="text-bear">*</span>
                </label>
                {noOperatorsAvailable ? (
                  <div className="flex items-center gap-2 rounded border border-warn/30 bg-warn/5 px-3 py-2 text-xs text-warn">
                    <AlertCircle size={13} className="shrink-0" />
                    No active operators available. Create an operator account first.
                  </div>
                ) : (
                  <select
                    value={form.operator_id}
                    onChange={(e) => setForm((f) => ({ ...f, operator_id: e.target.value }))}
                    className="input-base"
                    required
                  >
                    <option value="">Select operator…</option>
                    {operatorOptions.map((op) => (
                      <option key={op.id} value={op.id}>
                        {(op.full_name || op.username) + (op.email ? ` (${op.email})` : '')}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 rounded border border-amber/30 bg-amber/5 px-3 py-2 text-xs text-txt-secondary">
            <MailWarning size={14} className="text-amber mt-0.5 shrink-0" />
            <span>The backend generates a temporary password and attempts to send it by welcome email. Passwords are not entered here.</span>
          </div>

          {createError && (
            <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{createError}</p>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={createUser.isPending || noOperatorsAvailable}
              className="btn-primary disabled:opacity-50"
            >
              {createUser.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-3 gap-3">
        <StatCard label={isOperatorViewer ? 'Total Customers' : 'Total Users'} value={allUsers.length} icon={Users} />
        <StatCard label="Active"        value={activeCount}     accent="text-bull" />
        <StatCard label="Blocked"       value={blockedCount}    accent="text-bear" />
      </div>

      <div className="panel overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !allUsers.length ? (
          <div className="p-8">{isOperatorViewer
            ? <EmptyState title="No customers" description="No customers are assigned to your account yet." />
            : <EmptyState title="No users" description="No registered users yet." />}</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                {['ID', 'Full Name', 'Email', 'Broker', 'Roles', 'Status', 'Verified', 'Joined'].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-2xs font-mono text-txt-muted uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allUsers.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => navigate(ROUTES.adminUserDetail(u.id))}
                  className="border-b border-bg-border/30 hover:bg-bg-hover transition-colors cursor-pointer"
                >
                  <td className="py-3 px-4 font-mono text-txt-muted">{u.id}</td>
                  <td className="py-3 px-4 font-semibold text-txt-primary">{u.full_name || '—'}</td>
                  <td className="py-3 px-4 font-mono text-txt-secondary">{u.email || '—'}</td>
                  <td className="py-3 px-4 font-mono text-txt-secondary">{u.broker_name || '—'}</td>
                  <td className="py-3 px-4"><RoleList roles={u.roles} /></td>
                  <td className="py-3 px-4"><StatusBadge status={u.status} /></td>
                  <td className="py-3 px-4">
                    {u.email_verified
                      ? <CheckCircle size={14} className="text-bull" />
                      : <XCircle size={14} className="text-txt-muted" />}
                  </td>
                  <td className="py-3 px-4 font-mono text-txt-muted">{fmtDateTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
